/**
 * @rundown/policy — who may do what.
 *
 * Roles are VENDOR_* and CLIENT_*, never a company name. Which vendor runs a
 * given deployment is data (`workspaces.vendor_name`), because a role string
 * lives in a check constraint and in every membership row, and a display name
 * does not.
 *
 * Call sites check a capability, never a role: `@RequireCapability('billing.read')`
 * rather than `@RequireRole('admin')`. This table is the single place the matrix
 * lives, so "developers should not see Month end" is one line here instead of a
 * refactor scattered across handlers.
 *
 * Roles are fixed in code on purpose. A database-driven RBAC editor is a product
 * in its own right, and nobody needs one at two workspaces.
 *
 * v0.5 §5.2 collapsed v0.3's six-role model down to a single `role` column and
 * the prototype flattened it further to a Client/Noorix toggle. This restores it.
 */
export const ROLES = [
    'vendor_admin',
    'vendor_lead',
    'vendor_developer',
    'client_owner',
    'client_requester',
];
export const VENDOR_ROLES = ['vendor_admin', 'vendor_lead', 'vendor_developer'];
export const CLIENT_ROLES = ['client_owner', 'client_requester'];
export function isVendorRole(role) {
    return VENDOR_ROLES.includes(role);
}
export const CAPABILITIES = [
    /** Month end, engineer grades, billing factors. Withheld from developers. */
    'billing.read',
    /** Settings: workspace, people, engineers, calendars, repos, integrations. */
    'workspace.admin',
    /** The Review inbox: confirm the AI's read, set type and estimate. */
    'request.review',
    /** Internal titles, internal-only requests, anything not visible_to_client. */
    'internal.read',
    /** Commit placements, set owners, start a sprint. */
    'planning.write',
    /** Move cards across Board columns and log hours. */
    'board.write',
    /** Drag the backlog — rank, not priority, decides order. */
    'backlog.rank',
    /** Suggest a placement in Plan. A suggestion, never binding. */
    'plan.suggest',
    'changeofplan.raise',
    'hotfix.request',
    /**
     * Create projects (the bigger deliverables, like UI Revamp or AI Workflows)
     * and group requests into them as parts.
     *
     * v0.3 and Spec v0.5 §1 both had this as "either side", on the reasoning that
     * the client knows what belongs together as well as the vendor does. It is
     * vendor-only now, by decision: a project is the unit month end is grouped by
     * and delivery progress is reported against, so a client regrouping a request
     * moves a line on an invoice summary. The client's say in what belongs
     * together is the conversation on the request, and the vendor acts on it —
     * the same shape as priority, which the client asks for and the vendor
     * confirms (§5.5).
     */
    'project.manage',
    'request.create',
    'message.write',
];
/**
 * Project Lead is deliberately identical to Admin. That is safe here only
 * because there is no money in the system: no rates, no retainer fees, no dollar
 * amounts. If money ever arrives, this is the line that has to change first.
 */
const VENDOR_ADMIN = [
    'billing.read',
    'workspace.admin',
    'request.review',
    'internal.read',
    'planning.write',
    'board.write',
    'backlog.rank',
    'plan.suggest',
    'changeofplan.raise',
    'hotfix.request',
    'project.manage',
    'request.create',
    'message.write',
];
/** Everything except billing: no Month end, no grades, no factors. */
const VENDOR_DEVELOPER = [
    'request.review',
    'internal.read',
    'planning.write',
    'board.write',
    'backlog.rank',
    'project.manage',
    'request.create',
    'message.write',
];
const CLIENT_OWNER = [
    'backlog.rank',
    'plan.suggest',
    'changeofplan.raise',
    'hotfix.request',
    'request.create',
    'message.write',
];
const CLIENT_REQUESTER = ['request.create', 'message.write'];
export const ROLE_CAPABILITIES = {
    vendor_admin: new Set(VENDOR_ADMIN),
    vendor_lead: new Set(VENDOR_ADMIN),
    vendor_developer: new Set(VENDOR_DEVELOPER),
    client_owner: new Set(CLIENT_OWNER),
    client_requester: new Set(CLIENT_REQUESTER),
};
export function can(role, capability) {
    return ROLE_CAPABILITIES[role].has(capability);
}
/** A user may hold one role per workspace, but guards take a set for future-proofing. */
export function capabilitiesFor(roles) {
    const granted = new Set();
    for (const role of roles) {
        for (const capability of ROLE_CAPABILITIES[role])
            granted.add(capability);
    }
    return granted;
}
export function canAny(roles, capability) {
    return roles.some((role) => can(role, capability));
}
/**
 * Role names carry no company in them, and neither do these labels.
 *
 * They used to read "Noorix Admin". One deployment serves one vendor today,
 * but the vendor's NAME belongs in data — `workspaces.vendor_name` — not in a
 * role string that lives in a database check constraint and in every
 * membership row. Renaming those later means a data migration; renaming a
 * label is a string.
 */
export const ROLE_LABELS = {
    vendor_admin: 'Admin',
    vendor_lead: 'Project Lead',
    vendor_developer: 'Developer',
    client_owner: 'Client Owner',
    client_requester: 'Client Requester',
};
/**
 * What a role means, in a sentence an admin can act on.
 *
 * These live here rather than in the web app for the same reason the matrix
 * does: the screen that assigns a role and the table that decides what it
 * grants must not be able to disagree. A description maintained beside a
 * `<select>` drifts from the matrix the first time a capability moves, and the
 * drift is invisible — the UI keeps confidently describing a permission the
 * user no longer has.
 *
 * Written as consequences rather than feature lists. "Cannot see Month end" is
 * what an admin is actually choosing between; "lacks billing.read" is not.
 */
export const ROLE_DESCRIPTIONS = {
    vendor_admin: 'Full access, including Month end, engineer grades and every setting on this workspace.',
    vendor_lead: 'The same access as an Admin. Separate today only so the two can be told apart later.',
    vendor_developer: 'Builds and delivers: review, planning, the Board and hours. No Month end, no grades, no settings.',
    client_owner: 'The client’s decision-maker: ranks the backlog, suggests placements, raises hotfixes and changes of plan. Cannot regroup work into projects — that is what month end is split by.',
    client_requester: 'Posts requests and joins the conversation. Cannot reorder the backlog.',
};
/**
 * What each capability actually lets someone do.
 *
 * Phrased from the holder's point of view so a row reads as a sentence about a
 * person: "Reorder the backlog", not "backlog ranking". Used to explain a role
 * before it is granted, which is the moment the explanation is worth anything.
 */
export const CAPABILITY_LABELS = {
    'billing.read': 'See Month end, engineer grades and billing factors',
    'workspace.admin': 'Change settings: people, engineers, calendars and integrations',
    'request.review': 'Confirm what a request is, and what it will cost',
    'internal.read': 'See internal titles and requests hidden from the client',
    'planning.write': 'Commit work into a sprint and start it',
    'board.write': 'Move cards across the Board and log hours',
    'backlog.rank': 'Reorder the backlog',
    'plan.suggest': 'Suggest a placement, without committing it',
    'changeofplan.raise': 'Raise a change of plan',
    'hotfix.request': 'Request a hotfix',
    'project.manage': 'Create projects and group requests into them (the vendor’s side only)',
    'request.create': 'Post a request',
    'message.write': 'Write in a request conversation',
};
//# sourceMappingURL=index.js.map